import * as React from 'react';
import Card from 'components/Card';
import { themeDark } from 'styles/theme';
import { rgba } from 'styles/utils/color';
import { calculateSplit } from 'utils/shared/trades/calculateSplit';
import { formatMoney, formatSignedMoney } from 'utils/shared/number/formatFlowMoney';

interface Props {
  className?: string;
  title?: string;
  /**
   * Replaces the plain title, for surfaces that put a control there. The denominator
   * steps aside when one is supplied — a control is wider than a word, and both sides
   * of the denominator are already spelled out in the ledger below.
   */
  titleNode?: React.ReactNode;
  variant?: 'default' | 'compact';
  bullishPremium?: number;
  bearishPremium?: number;
  neutralPremium?: number;
}

// Graph ramp — fills, glows and the bead only. #f04662 is 4.34:1 on card paper and
// fails AA as text, so it never carries a glyph; the text ramp below does that.
const GRAPH_PUT = '#f04662';
const GRAPH_CALL = '#17be64';
const FLAT = '#8A8F98';

// A low-alpha ramp: bearish red on the left, a near-neutral seam at the origin, bullish
// green on the right. Deliberately quiet — the track states the axis; the fill and bead
// carry the reading. A louder track competes with them and reads as heavy.
const RAIL_BACKGROUND = [
  'linear-gradient(90deg,',
  'rgba(240,70,98,0.22) 0%,',
  'rgba(240,70,98,0.05) 40%,',
  'rgba(255,255,255,0.04) 50%,',
  'rgba(23,190,100,0.05) 60%,',
  'rgba(23,190,100,0.22) 100%)',
].join(' ');

/**
 * Net premium is derived here from the two premium sums rather than taken as a prop.
 *
 * `netPremiumShare` exists in this codebase in two incompatible units — the ticker and
 * sector aggregates emit it through `toPercent` as a whole percent, while
 * `FlowPageContainer` emits the raw -1..1 fraction. Same field name, 100x apart. Taking
 * bullish/bearish dollars instead is unit-unambiguous and cannot drift.
 *
 * Everything geometric comes from ONE quantity, `position`: it is both the bead's
 * placement and the bullish share, so the bar and the printed percentages are incapable
 * of disagreeing.
 */
const useNetPremium = (bullishPremium: number, bearishPremium: number) => {
  // Percentages come from `calculateSplit`, the same primitive the mix card uses, so a
  // side holding real premium is floored at 1% instead of rounding to 0% beside its own
  // non-zero dollar figure. The BAR still uses the unrounded share, so the bead is exact.
  const split = calculateSplit(bearishPremium, bullishPremium);
  const hasFlow = split.total > 0;

  return {
    bearish: split.primary,
    bullish: split.secondary,
    directionalTotal: split.total,
    hasFlow,
    position: hasFlow ? split.secondaryShare * 100 : 50,
    bearishPercentage: split.primaryPercentage,
    bullishPercentage: split.secondaryPercentage,
    net: split.secondary - split.primary,
  };
};

interface RailProps {
  position: number;
  net: number;
  hasFlow: boolean;
  accent: string;
  compact?: boolean;
}

const Rail: React.FC<RailProps> = ({ position, net, hasFlow, accent, compact }) => {
  // The bead centre and the fill's moving edge are the same expression, so they stay
  // exactly aligned at every value and every card width. 7px clears the bead radius so
  // it never overhangs the channel's rounded corner.
  const edge = `clamp(7px, ${position}%, calc(100% - 7px))`;
  const edgeFromRight = `clamp(7px, ${100 - position}%, calc(100% - 7px))`;
  const isBearish = net < 0;

  // A thin channel with the bead riding slightly proud of it, rather than a fat gutter
  // the bead sits down inside. The slimmer track is the single biggest lightening move on
  // this card — same information, a fraction of the ink.
  const trackH = compact ? 7 : 8;
  const beadSize = compact ? 8 : 9;

  return (
    // No overflow-hidden anywhere in this subtree — the fill and bead glows are meant
    // to bleed past the channel into the surrounding gap. That bleed is the effect.
    <div className={`relative ${compact ? 'h-3' : 'h-[14px]'}`}>
      <div
        className="absolute inset-x-0 top-1/2 -translate-y-1/2 rounded-full"
        style={{
          height: trackH,
          background: RAIL_BACKGROUND,
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.05)',
        }}
      />
      {/* Quartile reference marks, sized to the track so they read as scale, not posts. */}
      {[25, 75].map((tick) => (
        <div
          key={tick}
          className="absolute top-1/2 w-px -translate-x-1/2 -translate-y-1/2 bg-white/[0.12]"
          style={{ left: `${tick}%`, height: trackH }}
        />
      ))}
      {hasFlow && net !== 0 && (
        <div
          className="absolute top-1/2 -translate-y-1/2 rounded-full transition-[left,right] duration-200 ease-out motion-reduce:transition-none"
          style={
            isBearish
              ? {
                  height: trackH,
                  left: edge,
                  right: '50%',
                  background: `linear-gradient(90deg, ${accent} 0%, ${rgba(accent, 0.3)} 100%)`,
                  boxShadow: `0 0 8px ${rgba(accent, 0.3)}`,
                }
              : {
                  height: trackH,
                  left: '50%',
                  right: edgeFromRight,
                  background: `linear-gradient(90deg, ${rgba(accent, 0.3)} 0%, ${accent} 100%)`,
                  boxShadow: `0 0 8px ${rgba(accent, 0.3)}`,
                }
          }
        />
      )}
      {/* Origin datum, painted over the fill so the zero reference never disappears. Kept
          a hair taller than the track so it still reads as a tick against a busy fill. */}
      <div
        className="absolute left-1/2 top-1/2 w-px -translate-x-1/2 -translate-y-1/2 bg-white/40"
        style={{ height: trackH + 3 }}
      />
      <div
        className="absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full transition-[left] duration-200 ease-out motion-reduce:transition-none"
        style={{
          left: edge,
          width: beadSize,
          height: beadSize,
          backgroundColor: accent,
          // A 1px paper hairline parts the bead from the fill beneath it.
          borderWidth: 1,
          borderColor: themeDark.color.paper,
          boxShadow: hasFlow
            ? `0 0 0 1px ${rgba(accent, 0.8)}, 0 0 6px ${rgba(accent, 0.4)}`
            : 'none',
        }}
      />
    </div>
  );
};

/**
 * Net premium — the directional lean of the visible flow, denominated in dollars.
 *
 * Takes no metricMode: renders identically in Total and Net so the card row never swaps
 * widgets underneath the user. Owns money exclusively — no contract count appears here,
 * which is what keeps the call/put mix card beside it from restating it.
 */
const PutCallCard: React.FC<React.PropsWithChildren<Props>> = ({
  className,
  title = 'Net Premium',
  titleNode,
  variant = 'default',
  bullishPremium = 0,
  bearishPremium = 0,
  neutralPremium = 0,
}) => {
  const {
    bullish,
    bearish,
    directionalTotal,
    hasFlow,
    position,
    bullishPercentage,
    bearishPercentage,
    net,
  } = useNetPremium(bullishPremium, bearishPremium);
  const mid = Math.max(neutralPremium, 0);
  const compact = variant === 'compact';

  const accent = !hasFlow || net === 0 ? FLAT : net < 0 ? GRAPH_PUT : GRAPH_CALL;
  const textTone =
    !hasFlow || net === 0 ? 'text-palette-black-7' : net < 0 ? 'text-put-text' : 'text-call-text';
  const verdict = !hasFlow
    ? mid > 0
      ? 'No Direction'
      : 'No Flow'
    : bullishPercentage > 50
    ? 'Bullish'
    : bullishPercentage < 50
    ? 'Bearish'
    : 'Balanced';

  const verdictPill = (
    <span
      className={`inline-flex shrink-0 items-center rounded-full px-2 py-[3px] text-[9px] font-semibold uppercase leading-none tracking-[0.1em] ${
        hasFlow && net !== 0 ? textTone : 'text-palette-black-7'
      }`}
      style={{ backgroundColor: rgba(accent, hasFlow && net !== 0 ? 0.14 : 0.09) }}
    >
      {verdict}
    </span>
  );

  const content = (
    <>
      {/* Title row: the label, or on ticker research the view toggle. On the plain card the
          verdict tag lives here on the right; when a toggle owns the row it would crowd the
          tabs, so the tag drops to the value line instead (see below). */}
      <div
        className={
          titleNode
            ? 'flex items-center justify-between gap-2'
            : 'flex h-4 items-center justify-between gap-2'
        }
      >
        {titleNode || (
          <span className="truncate text-[11px] font-medium leading-4 text-palette-black-7">
            {title}
          </span>
        )}
        {!titleNode && verdictPill}
      </div>

      {/* The number. On the plain card its denominator rides alongside as quiet context;
          under a toggle the verdict tag takes that slot instead. Either way the denominator
          is spelled out in the ledger below, and the compact sidebar drops it — 200px wide. */}
      <div
        className={`flex justify-between gap-2 ${titleNode ? 'items-center' : 'items-baseline'} ${
          compact ? 'mt-1.5 h-4' : 'mt-2 h-[18px]'
        }`}
      >
        <span
          className={`shrink-0 font-semibold tabular-nums ${
            compact ? 'text-[14px] leading-4' : 'text-[16px] leading-[18px]'
          } ${textTone}`}
        >
          {hasFlow ? formatSignedMoney(net) : '$0'}
        </span>
        {titleNode
          ? verdictPill
          : (hasFlow || mid > 0) &&
            !compact && (
              <span
                className="shrink-0 text-[10px] font-medium leading-[11px] tracking-[0.06em] text-palette-black-7"
                title="Bullish plus bearish premium. The split compares the two sides."
              >
                <span className="tabular-nums text-palette-black-8">
                  {formatMoney(directionalTotal)}
                </span>{' '}
                DIRECTIONAL
              </span>
            )}
      </div>

      <div
        className="mt-2"
        role="img"
        aria-label={
          hasFlow
            ? `Net premium ${formatSignedMoney(
                net,
              )}. ${bearishPercentage} percent bearish, ${bullishPercentage} percent bullish.`
            : 'No directional premium in view.'
        }
      >
        <Rail position={position} net={net} hasFlow={hasFlow} accent={accent} compact={compact} />
      </div>

      <div className="mt-2 grid h-3 grid-cols-[1fr_auto_1fr] items-baseline">
        <span className="flex items-baseline gap-1 justify-self-start leading-[12px]">
          {hasFlow && (!compact || mid === 0) && (
            <span className="text-[11px] font-medium tabular-nums text-put-text">
              {bearishPercentage}%
            </span>
          )}
          <span className="text-[11px] font-semibold tabular-nums text-palette-black-8">
            <span className="sr-only">Bearish </span>
            {formatMoney(bearish)}
          </span>
        </span>
        <span className="justify-self-center text-[9px] font-medium tabular-nums leading-[12px] tracking-[0.06em] text-palette-black-6">
          {mid > 0 ? `MID ${formatMoney(mid)}` : ''}
        </span>
        <span className="flex items-baseline justify-end gap-1 justify-self-end leading-[12px]">
          <span className="text-[11px] font-semibold tabular-nums text-palette-black-8">
            <span className="sr-only">Bullish </span>
            {formatMoney(bullish)}
          </span>
          {hasFlow && (!compact || mid === 0) && (
            <span className="text-[11px] font-medium tabular-nums text-call-text">
              {bullishPercentage}%
            </span>
          )}
        </span>
      </div>
    </>
  );

  if (compact) {
    return (
      <div className={`w-full border-t border-palette-border pb-2 pt-4 ${className || ''}`}>
        {content}
      </div>
    );
  }

  return (
    // Inline padding beats the Card's baked-in `p-4` without betting on Tailwind's
    // utility emission order. The whole height budget rests on it.
    <Card
      className={`flex w-full flex-col justify-center md:w-1/3 ${className || ''}`}
      style={{ padding: 12 }}
    >
      {content}
    </Card>
  );
};

export default PutCallCard;
